import { useEffect, useId, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { Button, FormField, Input, Select, Tooltip, useToast } from '@vd/ui';
import type { ProjectTicketType } from '@vd/types';
import { api } from '../../../lib/api';
import { TypeMarker } from '../../../features/ticketTypes/TypeMarker';
import styles from '../Settings.module.css';
import mapping from './JiraMappingTab.module.css';
import { JiraProjectCombobox } from './JiraProjectCombobox';
import { JiraAutoSendSection } from './JiraAutoSendSection';
import { JiraExportHelp, JiraNeedsTeam } from '../../../features/jira/JiraExportHelp';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../features/auth/AuthProvider';
import { useQuery } from '@tanstack/react-query';
import { formatDay } from '../../../lib/time';

/**
 * Which Jira site this project's tickets go to, and whose sign-in connected
 * it, with the way to the workspace's Jira settings (Jason, 14 Oct): with
 * several workspaces on several Jira sites, the tab says which one this is.
 * This tab is owners' only, as the settings it links to are.
 */
export function JiraSiteLine({ companyId }: { companyId: string }) {
  const { setCurrentCompany } = useAuth();
  const connection = useQuery({ queryKey: ['jira-connection', companyId], queryFn: () => api.getJiraConnection(companyId), staleTime: 30_000 });
  // Jira settings are the selected workspace's: the link names this project's
  // workspace, which settings selects as they open, for a new tab or a copied
  // link too (Codex review round 27), and selects it now on a plain click, as
  // a ticket opened from another workspace does (round 26).
  const settings = (label: string) => <Link to={`/company/settings/jira?workspace=${encodeURIComponent(companyId)}`}
    onClick={() => setCurrentCompany(companyId)}>{label}</Link>;
  const state = connection.data;
  if (!state) {
    if (!connection.isError) return null;
    return <p className={mapping.site} role="alert">
      Could not load which Jira site this workspace is connected to.{' '}
      <button type="button" onClick={() => void connection.refetch()}>Retry</button> {settings('Jira settings')}
    </p>;
  }
  if (state.status !== 'connected' || !state.siteUrl) {
    return <p className={mapping.site}>
      This workspace is not connected to Jira{state.status === 'disconnected' ? ' any more' : ''}. {settings('Connect in Jira settings')}
    </p>;
  }
  const site = siteHost(state.siteUrl);
  const since = state.connectedAt ? formatDay(state.connectedAt) : null;
  return <p className={mapping.site}>
    Sends to <strong>{site}</strong>
    {state.connectedByName ? <>, connected by {state.connectedByName}{since ? ` on ${since}` : ''}</> : since ? <>, connected on {since}</> : null}.{' '}
    {settings('Jira settings')}
  </p>;
}

const siteHost = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

type Metadata = Awaited<ReturnType<typeof api.getJiraMetadata>>;
type MappingState = Awaited<ReturnType<typeof api.getJiraMapping>>;
/** Toado ticket type id to the Jira issue type id it is filed as; "" means Everything else. */
type Rows = Record<string, string>;

/**
 * The ticket types that get a row: every type the project offers, and any
 * type hidden since that already has one, so a saved row never disappears
 * from view while it still sends tickets somewhere.
 */
export function typesWithRows(types: ProjectTicketType[], rows: Rows): ProjectTicketType[] {
  return types.filter((type) => !type.hidden || Boolean(rows[type.id]));
}

const sameName = (name: string) => name.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * The Jira issue types a kind of ticket goes to when no Jira type has its very
 * same name (Jason, 13 Oct): Feature and New Feature both ways round, since
 * either side may use either name, and Improvement to Task, which Jira
 * projects have where they have no Improvement type. The owner can change any
 * of them. A Map, not an object: a ticket type an owner names "constructor" or
 * "__proto__" must not find an object's own machinery (Codex review round 6).
 */
const EQUIVALENT_NAMES = new Map<string, string[]>([
  ['feature', ['new feature']],
  ['new feature', ['feature']],
  ['improvement', ['task']],
]);

/**
 * The rows a newly chosen Jira project fills in by itself (Jason, 13 Oct):
 * each kind of ticket the project offers whose name is a Jira issue type's
 * too, letter case and spacing aside, so Bug goes to Bug and "New feature" to
 * "New Feature"; failing that, a type named as its equivalent, so Feature
 * goes to New Feature. Anything without a match is left for the owner.
 */
export function matchingRows(types: ProjectTicketType[], issueTypes: Array<{ id: string; name: string }>): Rows {
  const byName = new Map(issueTypes.map((issueType) => [sameName(issueType.name), issueType.id]));
  const rows: Rows = {};
  for (const type of types) {
    if (type.hidden) continue;
    const name = sameName(type.name);
    const match = byName.get(name) ?? (EQUIVALENT_NAMES.get(name) ?? []).map((other) => byName.get(other)).find(Boolean);
    if (match) rows[type.id] = match;
  }
  return rows;
}

/** What the default labels do, for the info bubble beside them (Jason, 13 Oct). */
export const LABELS_HELP = 'Added to every Jira issue this project exports, so your team can filter and search for them in Jira, '
  + 'for example labels = toado. Separate labels with commas. A label can\'t contain a space or a comma, so write '
  + 'customer-reported rather than customer reported. Up to 20. Toado also adds its own toado- label with the ticket\'s id '
  + 'to every issue, which keeps a retried export from filing the same ticket twice.';

/**
 * An info button whose tooltip explains the default labels. The tooltip is
 * drawn only while open, so the button also carries the text as its
 * description, which a screen reader reads when it is focused.
 */
function LabelsHelp() {
  const describedBy = useId();
  return <>
    <Tooltip content={LABELS_HELP} multiline placement="top">
      <button type="button" className={mapping.info} aria-label="About default labels" aria-describedby={describedBy}>
        <Info size={14} aria-hidden />
      </button>
    </Tooltip>
    <span id={describedBy} className={mapping.srOnly}>{LABELS_HELP}</span>
  </>;
}

/**
 * What stands in for the form while the Jira projects, the ticket types and
 * the saved mapping load (Jason, 13 Oct): the shapes of the fields to come,
 * so nothing jumps when they arrive, and a word for a screen reader.
 */
function MappingSkeleton() {
  return (
    <div className={mapping.skeleton} aria-busy="true">
      <span role="status" className={mapping.srOnly}>Loading the Jira projects and ticket types...</span>
      <span className={`${mapping.bone} ${mapping.boneLabel}`} />
      <span className={`${mapping.bone} ${mapping.boneField}`} />
      <span className={`${mapping.bone} ${mapping.boneLegend}`} />
      {[0, 1, 2].map((row) => (
        <span key={row} className={mapping.row}>
          <span className={`${mapping.bone} ${mapping.boneName}`} />
          <span className={`${mapping.bone} ${mapping.boneField}`} />
        </span>
      ))}
    </div>
  );
}

/**
 * The Jira export tab of one Toado project. Each project gets its own form,
 * keyed here rather than by whoever renders it: a form carried from one
 * project to the next kept the first one's mapping, and a Save before the
 * second one's had loaded wrote the first project's mapping over it (Codex
 * review round 3).
 */
export function JiraMappingTab({ projectId, companyId }: { projectId: string; companyId: string }) {
  // The same read the site line makes. Below the Team plan the tab explains
  // Jira export and offers the upgrade instead of a mapping (TOADO-713).
  const connection = useQuery({ queryKey: ['jira-connection', companyId], queryFn: () => api.getJiraConnection(companyId), staleTime: 30_000 });
  // The form waits for the plan's first answer, so a workspace below Team never
  // sees it flash or asks Jira for its projects. A read that failed shows the
  // form as before, and never the wait again: the form's site line reads once
  // more as it mounts, and going back to waiting would unmount it, its read
  // would fail, and the two would take turns for ever.
  if (connection.isPending && connection.errorUpdateCount === 0) {
    return <>
      <h1 className={styles.h1}>Jira export</h1>
      <MappingSkeleton />
    </>;
  }
  if (connection.data?.planHasJira === false) {
    return <>
      <h1 className={styles.h1}>Jira export</h1>
      <MappingBelowTeam key={projectId} projectId={projectId} />
      <JiraNeedsTeam companyId={companyId} />
    </>;
  }
  return <JiraMappingForm key={`${companyId}:${projectId}`} projectId={projectId} companyId={companyId} />;
}

/** What Remove mapping does, beside the button (Jason, 13 Oct). */
export const REMOVE_HELP = 'No new exports from this project until it is mapped again; one already under way may still finish. Issues already in Jira stay there, linked to their tickets.';

/**
 * A mapping made before the workspace left the Team plan: shown, and it can
 * still be removed, as the server allows (Codex review round 1 of TOADO-713).
 * Nothing shows for a project that was never mapped.
 */
function MappingBelowTeam({ projectId }: { projectId: string }) {
  const removeHelp = useId();
  const toast = useToast();
  const [current, setCurrent] = useState<MappingState | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    void api.getJiraMapping(projectId).then((saved) => { if (!cancelled) setCurrent(saved); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [projectId, attempt]);
  // A read that failed may hide a mapping, and with it Remove (Codex review round 2).
  if (failed) {
    return <section className={styles.section}>
      <p role="alert">Could not load this project's Jira mapping.</p>
      <Button variant="secondary" onClick={() => setAttempt((count) => count + 1)}>Retry loading the mapping</Button>
    </section>;
  }
  if (!current?.mapping) return null;
  const remove = async () => {
    setBusy(true);
    try { await api.deleteJiraMapping(projectId); setCurrent({ status: 'not_mapped' }); }
    catch (e) { toast.push({ variant: 'danger', title: 'Could not remove the Jira mapping', description: (e as Error).message }); }
    finally { setBusy(false); }
  };
  return <section className={styles.section}>
    <p>Mapped to <strong>{current.mapping.jiraProjectName}</strong> ({current.mapping.jiraProjectKey}). Sending more tickets needs the Team plan; a send already under way may still finish.</p>
    <div className={mapping.remove}>
      <p id={removeHelp} className={mapping.removeHelp}>{REMOVE_HELP}</p>
      <Button variant="secondary" disabled={busy} onClick={() => void remove()} aria-describedby={removeHelp}>Remove mapping</Button>
    </div>
  </section>;
}

function JiraMappingForm({ projectId, companyId }: { projectId: string; companyId: string }) {
  const removeHelp = useId();
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const [current, setCurrent] = useState<MappingState | null>(null);
  const [ticketTypes, setTicketTypes] = useState<ProjectTicketType[]>([]);
  const [typesLoad, setTypesLoad] = useState<'loading' | 'loaded' | 'failed'>('loading');
  const [typesAttempt, setTypesAttempt] = useState(0);
  const [mappingFailed, setMappingFailed] = useState(false);
  const [mappingAttempt, setMappingAttempt] = useState(0);
  const [jiraProjectId, setJiraProjectId] = useState('');
  const [issueTypeId, setIssueTypeId] = useState('');
  const [rows, setRows] = useState<Rows>({});
  // The names the saved mapping gave its issue types, for one Jira no longer has.
  const [savedNames, setSavedNames] = useState<Record<string, string>>({});
  const [labels, setLabels] = useState('');
  const [busy, setBusy] = useState(false);
  // Not loading the Jira projects is the tab's state, and stays on it; a save
  // or a removal that failed is said in a red toast as it fails (Jason, 13 Oct).
  const [metadataFailed, setMetadataFailed] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    setMappingFailed(false);
    void api.getJiraMapping(projectId).then((saved) => {
      if (cancelled) return;
      setCurrent(saved);
      if (saved.mapping) {
        setJiraProjectId(saved.mapping.jiraProjectId);
        setIssueTypeId(saved.mapping.issueTypeId);
        const savedRows = Object.entries(saved.mapping.issueTypesByTicketType ?? {});
        setRows(Object.fromEntries(savedRows.map(([type, issueType]) => [type, issueType.id])));
        setSavedNames(Object.fromEntries([[saved.mapping.issueTypeId, saved.mapping.issueTypeName],
          ...savedRows.map(([, issueType]) => [issueType.id, issueType.name])]));
        setLabels(saved.mapping.defaultLabels.join(', '));
      }
    }).catch(() => { if (!cancelled) setMappingFailed(true); });
    return () => { cancelled = true; };
  }, [projectId, mappingAttempt]);
  useEffect(() => {
    let cancelled = false;
    void api.getJiraMetadata(companyId).then((live) => {
      if (!cancelled) setMetadata(live);
    }).catch(() => { if (!cancelled) setMetadataFailed(true); });
    return () => { cancelled = true; };
  }, [companyId]);
  useEffect(() => {
    let cancelled = false;
    setTypesLoad('loading');
    void api.listProjectTicketTypes(projectId).then((types) => {
      if (cancelled) return;
      setTicketTypes(types.items);
      setTypesLoad('loaded');
    }).catch(() => { if (!cancelled) setTypesLoad('failed'); });
    return () => { cancelled = true; };
  }, [projectId, typesAttempt]);

  const selectedProject = useMemo(() => metadata?.projects.find((project) => project.id === jiraProjectId), [metadata, jiraProjectId]);
  const issueTypes = selectedProject?.issueTypes?.filter((type) => !type.subtask) ?? [];
  const shownTypes = typesWithRows(ticketTypes, rows);
  // A choice the Jira project no longer offers (its admin removed the type) is
  // shown as such and must be replaced before saving; a select left showing a
  // fallback while it held the old id was a save that failed for no visible
  // reason (Codex and z.ai review round 1).
  const offered = new Set(issueTypes.map((type) => type.id));
  const isGone = (id: string) => id !== '' && selectedProject !== undefined && !offered.has(id);
  const goneChoices = [issueTypeId, ...shownTypes.map((type) => rows[type.id] ?? '')].filter(isGone);
  // A saved Jira project the site no longer lists is flagged the same way.
  const projectGone = metadata !== null && jiraProjectId !== '' && selectedProject === undefined;
  // Saving waits for everything it is judged against: the saved mapping, the
  // Jira projects and the project's ticket types. Before then, or after one
  // failed, a stale row could be sent without being shown (Codex review round 2).
  const ready = current !== null && metadata !== null && typesLoad === 'loaded';
  // Still on the way, with nothing failed: the form's shape stands in for it.
  const loading = !ready && !mappingFailed && !metadataFailed && typesLoad !== 'failed';

  const chooseProject = (id: string) => {
    if (id === jiraProjectId) return;
    // Issue types belong to a Jira project, so every choice made for the last
    // one goes, and the new one's same-named types are chosen to start with.
    // Only here, on a choice: a saved mapping is shown as it was saved.
    const chosen = metadata?.projects.find((project) => project.id === id);
    setJiraProjectId(id);
    setIssueTypeId('');
    setRows(matchingRows(ticketTypes, chosen?.issueTypes?.filter((type) => !type.subtask) ?? []));
  };
  const save = async () => {
    setBusy(true);
    try {
      const result = await api.saveJiraMapping(projectId, { jiraProjectId, issueTypeId,
        issueTypesByTicketType: Object.fromEntries(Object.entries(rows).filter(([, id]) => id !== '')),
        defaultLabels: labels.split(',').map((label) => label.trim()).filter(Boolean) });
      setCurrent(result as MappingState);
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Could not save the Jira mapping', description: (e as Error).message });
    } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.deleteJiraMapping(projectId); setCurrent({ status: 'not_mapped' }); setJiraProjectId(''); setIssueTypeId(''); setRows({}); }
    catch (e) { toast.push({ variant: 'danger', title: 'Could not remove the Jira mapping', description: (e as Error).message }); }
    finally { setBusy(false); }
  };

  const issueTypeSelect = (value: string, onChange: (id: string) => void, empty: string, label?: string) => (
    // The design system's select, styled as the project box is (Jason, 13 Oct).
    <Select value={value} onChange={(event) => onChange(event.target.value)} disabled={!selectedProject || busy} aria-label={label}
      invalid={isGone(value)} aria-invalid={isGone(value) || undefined}>
      <option value="">{empty}</option>
      {isGone(value) && <option value={value}>{savedNames[value] ?? 'A type'} (no longer in this Jira project)</option>}
      {issueTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
    </Select>
  );

  return <>
    <h1 className={styles.h1}>Jira export</h1>
    <p className={styles.sub}>Map this Toado project to one live Jira project, and each kind of ticket to a Jira issue type. Ticket title becomes summary, description and capture metadata become ADF content, every picture becomes an attachment with its annotations drawn in, and Jira links back to Toado for evidence. <JiraExportHelp /></p>
    <JiraSiteLine companyId={companyId} />
    {current?.status === 'stale' && <p role="alert">This mapping belongs to a previous or disconnected Jira site. Choose a new mapping before exporting.</p>}
    {metadataFailed && <p role="alert">Could not load the Jira projects. Connect Jira in Company settings before choosing a new mapping.</p>}
    <section className={styles.section}>
      {loading ? <MappingSkeleton /> : <>
      <FormField label="Jira project">
        {/* Chosen only once everything has loaded: a choice fills in the rows
            from the ticket types, and one made before they arrived filled in
            nothing (Codex review round 5); a saved mapping arriving after a
            choice would also overwrite it. */}
        <JiraProjectCombobox projects={metadata?.projects ?? []} value={jiraProjectId} onChange={chooseProject}
          disabled={!ready || busy} />
      </FormField>
      {shownTypes.length > 0 && (
        <fieldset className={mapping.types}>
          <legend className={mapping.legend}>Issue type for each kind of ticket</legend>
          {shownTypes.map((type) => (
            <div key={type.id} className={mapping.row}>
              <span className={mapping.typeName}><TypeMarker glyph={type.glyph} height={18} />{type.name}</span>
              {issueTypeSelect(rows[type.id] ?? '', (id) => setRows((now) => ({ ...now, [type.id]: id })),
                'Use Everything else', `Jira issue type for ${type.name}`)}
            </div>
          ))}
        </fieldset>
      )}
      <FormField label={shownTypes.length > 0 ? 'Everything else' : 'Issue type'}
        help={shownTypes.length > 0 ? 'Tickets with no type, and kinds left on "Use Everything else".' : undefined}>
        {issueTypeSelect(issueTypeId, setIssueTypeId, 'Choose an issue type')}
      </FormField>
      </>}
      <FormField label="Default labels" help={<>Optional, comma-separated Jira labels. <LabelsHelp /></>}>
        {/* Off until the saved mapping is in, whose labels would replace
            anything typed before (Codex review round 8). */}
        <Input value={labels} onChange={(event) => setLabels(event.target.value)} disabled={!ready || busy} />
      </FormField>
      {mappingFailed && <>
        <p role="alert">Could not load the saved Jira mapping, so it can't be changed yet.</p>
        <Button variant="secondary" onClick={() => setMappingAttempt((count) => count + 1)}>Retry loading the mapping</Button>
      </>}
      {typesLoad === 'failed' && <>
        <p role="alert">Could not load this project's ticket types, so the mapping can't be saved yet.</p>
        <Button variant="secondary" onClick={() => setTypesAttempt((count) => count + 1)}>Retry</Button>
      </>}
      {projectGone && <p role="alert">The saved Jira project is no longer available on this site. Choose another.</p>}
      {goneChoices.length > 0 && (
        <p role="alert">Some issue types chosen here are no longer in {selectedProject?.name ?? 'this Jira project'}. Choose another where it says so, then save.</p>
      )}
      <div className={mapping.actions}>
        <Button variant="primary" disabled={!ready || !jiraProjectId || !issueTypeId || projectGone || goneChoices.length > 0 || busy} loading={busy} onClick={() => void save()}>Save Jira mapping</Button>
        {current?.mapping && (
          <div className={mapping.remove}>
            <p id={removeHelp} className={mapping.removeHelp}>{REMOVE_HELP}</p>
            <Button variant="secondary" disabled={busy} onClick={() => void remove()} aria-describedby={removeHelp}>Remove mapping</Button>
          </div>
        )}
      </div>
    </section>
    {/* Only for a saved, current mapping: auto-send goes with it (Jason, 18 Oct). */}
    {current?.status === 'mapped' && typesLoad === 'loaded' && <JiraAutoSendSection projectId={projectId} ticketTypes={ticketTypes} />}
  </>;
}
